import { useState, useEffect } from 'react';
import { staffAPI, confidentialAPI } from '../services/api';

export default function useComplianceData() {
  const [overtimeStats, setOvertimeStats] = useState(null);
  const [overtimeAlerts, setOvertimeAlerts] = useState([]);
  const [overtimeLoading, setOvertimeLoading] = useState(true);

  const [restDayStats, setRestDayStats] = useState(null);
  const [restDayLoading, setRestDayLoading] = useState(true);

  const [passExpiryData, setPassExpiryData] = useState(null);
  const [passExpiryLoading, setPassExpiryLoading] = useState(true);
  const [passExpiryModalFilter, setPassExpiryModalFilter] = useState(null);

  const [complianceExpiryData, setComplianceExpiryData] = useState(null);
  const [complianceExpiryLoading, setComplianceExpiryLoading] = useState(true);

  useEffect(() => {
    const fetchOvertimeData = async () => {
      try {
        setOvertimeLoading(true);
        const statsResponse = await staffAPI.getRealtimeOvertimeStats();
        const alertsResponse = await staffAPI.getOvertimeAlerts();
        setOvertimeStats(statsResponse.data);
        setOvertimeAlerts(alertsResponse.data);
      } catch (error) {
        console.error('❌ Overtime data failed to load:', error?.message);
        setOvertimeStats({
          currentMonth: { totalEmployees: 0, totalOvertimeHours: 0, averageOvertimeHours: 0 },
          atRiskCount: 0, criticalCount: 0, exceededCount: 0,
          projectedExceedCount: 0, watchCount: 0, complianceRate: 0,
        });
        setOvertimeAlerts([]);
      } finally {
        setOvertimeLoading(false);
      }
    };

    const fetchRestDayData = async () => {
      try {
        setRestDayLoading(true);
        const statsResponse = await staffAPI.getRestDayMonitoringStats();
        setRestDayStats(statsResponse.data);
      } catch (error) {
        console.error('Rest day monitoring data failed to load:', error?.message);
        setRestDayStats({
          totalMonitored: 0, compliantCount: 0, nonCompliantCount: 0,
          criticalCount: 0, warningCount: 0, watchCount: 0,
          activeAlertsCount: 0, complianceRate: 100,
        });
      } finally {
        setRestDayLoading(false);
      }
    };

    const fetchPassExpiryData = async () => {
      try {
        setPassExpiryLoading(true);
        const response = await confidentialAPI.getPassExpiryDashboard();
        setPassExpiryData(response.data);
      } catch (error) {
        console.error('❌ Pass expiry data failed to load:', error?.message);
        setPassExpiryData(null);
      } finally {
        setPassExpiryLoading(false);
      }
    };

    const fetchComplianceExpiryData = async () => {
      try {
        setComplianceExpiryLoading(true);
        const response = await confidentialAPI.getComplianceExpiryDashboard();
        setComplianceExpiryData(response.data);
      } catch (error) {
        console.error('Compliance expiry data failed to load:', error?.message);
        setComplianceExpiryData(null);
      } finally {
        setComplianceExpiryLoading(false);
      }
    };

    fetchOvertimeData();
    fetchRestDayData();
    fetchPassExpiryData();
    fetchComplianceExpiryData();
  }, []);

  // Escape key handler for pass expiry modal
  useEffect(() => {
    if (!passExpiryModalFilter) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setPassExpiryModalFilter(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [passExpiryModalFilter]);

  return {
    overtimeStats,
    overtimeAlerts,
    overtimeLoading,
    restDayStats,
    restDayLoading,
    passExpiryData,
    passExpiryLoading,
    passExpiryModalFilter,
    setPassExpiryModalFilter,
    complianceExpiryData,
    complianceExpiryLoading,
  };
}
